// src/components/shopping/ShoppingListItemRow.jsx
import React, { useCallback, useState, useMemo } from "react";
import {
  Grid,
  ListItem,
  TextField,
  IconButton,
  Typography,
  Box,
  Tooltip,
  Chip,
  Button,
  useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import EuroIcon from "@mui/icons-material/Euro";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocalGroceryStoreIcon from "@mui/icons-material/LocalGroceryStore";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import ProductSelectionModal from "./modal/ProductSelectionModal";
import { useLocationCtx } from "../../context/LocationContext";
import { resolveCountryId } from "../../utils/country";
import useShoppingItemSelection, {
  formatSelectedSelectionSummary,
} from "../../hooks/useShoppingItemSelection";

function getCurrencySymbol(countryId) {
  return countryId === "2" || countryId === 2 ? "$" : "€";
}
function convertPrice(price) {
  const num = Number(price);
  return isNaN(num) ? "-" : num.toFixed(2);
}

function toFiniteNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

// Αυγά: generic_product_id = 50
function isPieceProduct(genericProductId) {
  return String(genericProductId) === "50";
}

// Για εμφάνιση βάρους (g/kg για ΕΕ, lb/oz για US)
function readableWeight(qty, country) {
  const v = Number(qty || 0);

  // 🇺🇸 US: lb / oz
  if (country === "2" || country === 2) {
    if (v >= 453.6) {
      const lbs = v / 453.6; // 1 lb = 453.6 g
      return `${lbs.toFixed(2)} lb`;
    } else {
      const oz = v / 28.35; // 1 oz ≈ 28.35 g
      return `${oz.toFixed(1)} oz`;
    }
  }

  // 🇬🇷 / ΕΕ: g / kg
  if (v < 1000) return `${v.toFixed(0)} g`;
  return `${(v / 1000).toFixed(2)} kg`;
}

function readableBaseQuantity(qty, unit, country, lang) {
  const value = Number(qty || 0);
  const normalizedUnit = String(unit || "").toLowerCase();
  const isGreek = lang === "el" || lang === "gr";

  if (normalizedUnit === "piece") {
    return `${value.toFixed(0)} ${isGreek ? "τεμ." : value === 1 ? "pc" : "pcs"}`;
  }

  if (normalizedUnit === "ml") {
    if (country === "2" || country === 2) {
      const flOz = value / 29.5735295625;
      return `${flOz.toFixed(1)} fl oz`;
    }
    if (value >= 1000) return `${(value / 1000).toFixed(2)} L`;
    return `${value.toFixed(0)} ml`;
  }

  return readableWeight(value, country);
}

export default function ShoppingListItemRow({
  item,
  onUpdate,
  onItemUpdated,
  readOnly = false,
  canChangeSelection = true,
  onGate,
  priceChange = null,
  onOpenHistory,
  onAddToWatchlist,
  canAddToWatchlist = false,
  isFollowed = false,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { country: ctxCountry } = useLocationCtx();
  const resolvedCountryId = resolveCountryId({ locationCountry: ctxCountry }).id;
  const currencySymbol = getCurrencySymbol(resolvedCountryId);

  const country = resolvedCountryId;
  const isUS = country === "2" || country === 2;

  const language = i18n.language === "gr" ? "el" : i18n.language;
  const langKey = language === "el" ? "gr" : language;

  const productName =
    langKey === "gr"
      ? item.product_name_el || item.name_el || item.name
      : item.product_name_en || item.name_en || item.name;

  const genericName =
    (langKey === "gr"
      ? item.generic_product_name_el || item.generic_name_el
      : item.generic_product_name_en || item.generic_name_en) ||
    item.generic_product_name ||
    "";

  const pricing = item.pricing_snapshot || {};
  const {
    genericProducts,
    variants,
    openVariantsOnlyModal,
    submitSelection,
    loadingGenericProducts,
    loadingVariants,
    submittingSelection,
    selectionError,
    selectedVariantId,
    searchQuery,
    selectedGenericId,
    selectionMode,
    lockedGenericProductId,
    lockedGenericProductName,
    setSelectedGenericId,
    loadVariants,
    searchGenericProducts,
  } = useShoppingItemSelection({
    item,
    currencySymbol,
    onItemUpdated,
    onSelectionSettled: () => {
      if (onUpdate) onUpdate();
    },
  });
  const selectionSummary = formatSelectedSelectionSummary(item, currencySymbol);

  // Brand
  const brand =
    item.selected_variant_snapshot?.brand_name_el ||
    item.selected_variant_snapshot?.brand_name ||
    item.brand_name ||
    "";
  const headerTitle = brand ? `${brand} – ${productName}` : productName;

  // Store
  const selectedVariant = item.selected_variant_snapshot || item.selected_variant || {};
  const cityRow = selectedVariant.store_name || item.store_name || "";
  const chainRow = selectedVariant.store_chain || item.store_chain || "";
  let storeName = "-";



  if (chainRow && cityRow && chainRow !== cityRow) {
    storeName = `${chainRow} – ${cityRow}`;
  } else if (chainRow) {
    storeName = chainRow;
  } else if (cityRow) {
    storeName = cityRow;
  } else {
    storeName = item.store || "-";
  }
  const pricingScopeLabel = selectedVariant.pricing_scope_label || item.pricing_scope_label || "";
  const storeDisplayName = pricingScopeLabel && storeName !== "-"
    ? `${storeName} · ${pricingScopeLabel}`
    : storeName;

  const requiredUnitRaw = String(pricing.required_unit || item.required_unit || item.base_unit || "g").toLowerCase();
  const requiredUnit = requiredUnitRaw === "count" ? "piece" : requiredUnitRaw;
  const reqQtyBase = Number(pricing.required_qty ?? item.required_quantity ?? item.qty_needed_base ?? 0);

  const purchasedBase = Number(
    item.purchased_quantity ?? item.qty_purchased_base ?? 0
  );

  const [purchaseQtyGrams, setPurchaseQtyGrams] = useState(purchasedBase);

  const backendUnitKind = (pricing.unit_price_kind || item.unit_kind || item.unit_price_kind || "").toLowerCase();
  const isPiece = requiredUnit === "piece" || isPieceProduct(item.generic_product_id);
  const isPiecePricing =
    backendUnitKind === "piece" ||
    (item.normalized_unit || "").toLowerCase() === "piece" ||
    (selectedVariant.normalized_unit || "").toLowerCase() === "piece" ||
    item.price_per_piece != null ||
    selectedVariant.price_per_piece != null;
  const isKgMode = requiredUnit === "g" && !isPiece && reqQtyBase >= 1000;

  const [displayQty, setDisplayQty] = useState(() => {
    if (purchasedBase > 0) {
      if (isPiece) {
        return String(purchasedBase);
      }

      if (requiredUnit === "ml" && !isUS) {
        return String(purchasedBase);
      }

      if (isUS) {
        const oz = requiredUnit === "ml"
          ? purchasedBase / 29.5735295625
          : purchasedBase / 28.349523125;
        const val = oz.toFixed(1);
        return val.endsWith(".0") ? val.slice(0, -2) : val;
      }

      if (isKgMode) {
        return String(purchasedBase / 1000);
      }
      return String(purchasedBase);
    }
    return "";
  });


  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const updating = submittingSelection;

  const remainingBase = Math.max(reqQtyBase - purchaseQtyGrams, 0);
  const progressStatus = String(item.item_status || item.status || "pending").toLowerCase();
  const isCovered = Boolean(item.is_covered || item.is_completed || item.has_at_home_override || item.removed_by_user);

  // 🔹 Πληροφορίες πακέτων / καλαθιού από backend
  const packagesNeeded =
    pricing.packages_needed == null && item.packages_needed == null ? null : Number(pricing.packages_needed ?? item.packages_needed ?? 0);
  const leftoverQtyBase =
    item.leftover_qty_base ??
    item.leftover_qty_piece ??
    item.leftover_qty_ml ??
    item.leftover_qty_g ??
    0;

  const packageSizeLabel =
    pricing.package_size_text ||
    selectedVariant.package_size_text ||
    item.package_size_text ||
    (selectedVariant.package_size_value && selectedVariant.package_size_unit
      ? `${selectedVariant.package_size_value} ${selectedVariant.package_size_unit}`
      : pricing.package_qty_canonical
      ? readableBaseQuantity(pricing.package_qty_canonical, pricing.package_unit_canonical, country, i18n.language)
      : item.normalized_qty
      ? isPiecePricing
        ? `${Number(item.normalized_qty)} ${i18n.language === "el" || i18n.language === "gr" ? "τεμ." : "pcs"}`
        : readableBaseQuantity(item.normalized_qty, item.normalized_unit, country, i18n.language)
      : null);

  const unitPrice =
    toFiniteNumber(pricing.unit_price) ??
    toFiniteNumber(item.unit_price) ??
    toFiniteNumber(selectedVariant.price_per_piece) ??
    toFiniteNumber(selectedVariant.price_per_kg) ??
    toFiniteNumber(selectedVariant.price_per_liter) ??
    toFiniteNumber(item.price_per_piece) ??
    toFiniteNumber(item.price_per_kg) ??
    toFiniteNumber(item.price_per_liter) ??
    toFiniteNumber(selectedVariant.price) ??
    toFiniteNumber(item.price);

  // Θεωρητικό κόστος για την ακριβή απαιτούμενη ποσότητα
  const theoreticalCost =
    pricing.consumption_cost !== undefined && pricing.consumption_cost !== null
      ? Number(pricing.consumption_cost)
      : item.theoretical_cost !== undefined && item.theoretical_cost !== null
        ? Number(item.theoretical_cost)
      : item.calculated_cost !== undefined && item.calculated_cost !== null
        ? Number(item.calculated_cost)
        : item.total_cost !== undefined && item.total_cost !== null
          ? Number(item.total_cost)
          : null;

  // Πραγματικό κόστος καλαθιού (πακέτα)
  const basketCost =
    pricing.purchase_cost !== undefined && pricing.purchase_cost !== null
      ? Number(pricing.purchase_cost)
      : item.basket_cost !== undefined && item.basket_cost !== null
        ? Number(item.basket_cost)
      : item.calculated_cost !== undefined && item.calculated_cost !== null
        ? Number(item.calculated_cost)
        : theoreticalCost;

  const pieceUnitsNeeded = isPiece ? reqQtyBase : 0;
  const pieceUnitsRemaining = isPiece ? remainingBase : 0;
  const showDerivedEggTooltip = isPiece && isPiecePricing;
  const eggPricingTooltip = t("eggs_priced_per_piece");

  const handleDisplayQtyChange = (e) => {
    const val = e.target.value;
    setDisplayQty(val);

    if (val === "") {
      setPurchaseQtyGrams(0);
      return;
    }

    const numVal = Number(val);
    if (isNaN(numVal) || numVal < 0) return;

    if (isPiece) {
      setPurchaseQtyGrams(numVal);
    } else if (isUS) {
      setPurchaseQtyGrams(
        requiredUnit === "ml" ? numVal * 29.5735295625 : numVal * 28.349523125
      );
    } else if (isKgMode) {
      setPurchaseQtyGrams(numVal * 1000);
    } else {
      setPurchaseQtyGrams(numVal);
    }
  };

  const handleStoreModalConfirm = async (newVariantId) => {
    try {
      await submitSelection({
        variantId: newVariantId,
        genericProductId: selectedGenericId,
      });
      setStoreModalOpen(false);
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpenProductSelection = async () => {
    const initialGenericId = item?.generic_product_id || selectedGenericId || "";
    setStoreModalOpen(true);
    try {
      await openVariantsOnlyModal({
        generic_product_id: initialGenericId,
        generic_name: genericName,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleModalSearchChange = useCallback(async (value) => {
    try {
      await searchGenericProducts(value);
    } catch (error) {
      console.error(error);
    }
  }, [searchGenericProducts]);

  const handleModalSelectGeneric = useCallback(async (genericProductId) => {
    setSelectedGenericId(genericProductId);
    try {
      await loadVariants(genericProductId);
    } catch (error) {
      console.error(error);
    }
  }, [loadVariants, setSelectedGenericId]);

  const handleUpdatePurchase = async () => {
    try {
      const response = await api.shoppingList.updateItemProgress(item.id, {
        action: "partial_purchase",
        purchased_qty: purchaseQtyGrams,
        purchased_unit: requiredUnit,
      });
      if (onItemUpdated && response?.item) onItemUpdated(response.item);
      if (onUpdate) onUpdate();
    } catch (e) {
      console.error(e);
    }
  };

  const handleProgressAction = async (payload) => {
    try {
      const response = await api.shoppingList.updateItemProgress(item.id, payload);
      if (onItemUpdated && response?.item) onItemUpdated(response.item);
      if (onUpdate) onUpdate();
    } catch (e) {
      console.error(e);
    }
  };

  const unitPriceLabel = useMemo(() => {
    if (!unitPrice) return "--";

    const country = resolvedCountryId;
    const normalized = (selectedVariant.normalized_unit || item.normalized_unit || "").toLowerCase();
    const effectiveUnitKind = backendUnitKind || (isPiecePricing ? "piece" : normalized);
    const isLiquid = effectiveUnitKind === "l" || effectiveUnitKind === "liter" || effectiveUnitKind === "ml" || normalized.includes("ml") || normalized === "l";
    const isPieceUnit = effectiveUnitKind === "piece";

    if (country === "2" || country === 2) {
      if (isPieceUnit) {
        return `${convertPrice(unitPrice)}${currencySymbol} / piece`;
      }
      if (isLiquid) {
        const pricePerFlOz = unitPrice / 33.814;
        return `${convertPrice(pricePerFlOz)}${currencySymbol} / fl oz`;
      } else {
        const pricePerLb = unitPrice / 2.20462;
        return `${convertPrice(pricePerLb)}${currencySymbol} / lb`;
      }
    }

    if (isPieceUnit) {
      return `${convertPrice(unitPrice)}${currencySymbol} / piece`;
    }

    return isLiquid
      ? `${convertPrice(unitPrice)}${currencySymbol} / L`
      : `${convertPrice(unitPrice)}${currencySymbol} / kg`;
  }, [unitPrice, currencySymbol, item.normalized_unit, selectedVariant.normalized_unit, resolvedCountryId, backendUnitKind, isPiecePricing]);

  const warningMessage = useMemo(() => {
    const warnings = new Set(Array.isArray(item.warnings) ? item.warnings : []);
    const hasUnitPrice = [
      pricing.unit_price,
      pricing.price_per_kg,
      pricing.price_per_liter,
      pricing.price_per_piece,
      selectedVariant.price_per_kg,
      selectedVariant.price_per_liter,
      selectedVariant.price_per_piece,
      item.unit_price,
    ].some((value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) && numeric > 0;
    });
    if (hasUnitPrice) {
      warnings.delete("missing_unit_price");
    }
    if (warnings.has("missing_variant")) {
      return t("missing_variant_warning") || "No variant selected for this item.";
    }
    if (warnings.has("missing_package_size") && warnings.has("missing_package_price")) {
      return t("missing_price_package_warning") || "Missing package size and package price for the selected variant.";
    }
    if (warnings.has("missing_package_size") && warnings.has("missing_unit_price")) {
      return t("missing_package_warning") || "Missing package size for the selected variant.";
    }
    if (warnings.has("missing_package_size")) {
      return t("missing_package_warning") || "Missing package size for the selected variant.";
    }
    if (warnings.has("missing_package_price")) {
      return t("missing_package_price_warning") || "Missing package price for the selected variant.";
    }
    if (warnings.has("missing_unit_price")) {
      return t("missing_price_warning") || "Missing unit price for the selected variant.";
    }
    return null;
  }, [item.warnings, item.unit_price, pricing.price_per_kg, pricing.price_per_liter, pricing.price_per_piece, pricing.unit_price, selectedVariant.price_per_kg, selectedVariant.price_per_liter, selectedVariant.price_per_piece, t]);

  const selectionButtonLabel = selectedVariantId
    ? (t("change_product_selection") || "Αλλαγή προϊόντος")
    : (t("select_product_selection") || "Επιλογή προϊόντος");

  const priceChangeLabel = useMemo(() => {
    if (!priceChange || priceChange.delta == null) return null;
    const delta = Number(priceChange.delta || 0);
    const deltaPct = priceChange.delta_pct != null ? Number(priceChange.delta_pct) : null;
    const direction = delta < 0
      ? (t("price_drop") || "Price drop")
      : (t("price_increase") || "Price increase");
    const pctText = deltaPct != null ? ` ${Math.abs(deltaPct).toFixed(1)}%` : "";
    const deltaText = `${Math.abs(delta).toFixed(2)}${currencySymbol}`;
    return `${direction} ${deltaText}${pctText}`;
  }, [priceChange, t, currencySymbol]);

  return (
    <Grid item xs={12} sm={8} md={6} lg={4}>
      <ListItem
        sx={{
          flexDirection: "column",
          alignItems: "flex-start",
          px: { xs: 2, sm: 4 },
          py: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          boxShadow: "0 2px 16px 0 rgb(80 110 150 / 8%)",
          bgcolor: theme.palette.background.paper,
          width: "100%",
          minHeight: 230,
          mb: 2,
          position: "relative",
          transition: "box-shadow .2s",
          "&:hover": { boxShadow: "0 6px 32px 0 rgb(80 120 180 / 18%)" },
        }}
        divider
      >
        {/* Header με icon + brand + όνομα προϊόντος */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 1,
            width: "100%",
            pb: 1,
            borderBottom: "1px solid",
            borderColor: theme.palette.divider,
          }}
        >
          <LocalGroceryStoreIcon sx={{ color: "#689f38", fontSize: 32 }} />
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ lineHeight: 1.15, letterSpacing: "-0.5px" }}
            >
              {headerTitle}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.2 }}
            >
              {t("generic_product")}: <b>{genericName || "-"}</b>
            </Typography>
            {(selectionSummary.packageText || pricing.package_price != null) ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2 }}>
                <b>{selectionSummary.packageText || packageSizeLabel || "-"}</b>
                {pricing.package_price != null ? ` · ${convertPrice(pricing.package_price)}${currencySymbol}` : ""}
              </Typography>
            ) : null}
          </Box>
        </Box>

        {/* Supermarket / Store */}
        {(selectionSummary.store || storeDisplayName || item.store_chain || item.store_provider) ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 0.5,
              mb: 1.8,
            }}
          >
            <StorefrontIcon
              sx={{
                fontSize: 20,
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.primary.light
                    : theme.palette.primary.main,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.primary.light
                    : theme.palette.primary.main,
              }}
            >
              {t("selected_store") || "Επιλεγμένο κατάστημα"}:{" "}
              <b>{selectionSummary.store || storeDisplayName || item.store_chain || item.store_provider}</b>
              {item.store_chain && item.store_chain !== storeName && (
                <span style={{ marginLeft: 6, opacity: 0.8 }}>
                  ({item.store_chain})
                </span>
              )}
              {isUS && item.store_provider ? (
                <span style={{ marginLeft: 6, opacity: 0.8 }}>
                  {item.store_chain ? " · " : ""}{item.store_provider}
                </span>
              ) : null}
            </Typography>
          </Box>
        ) : null}

        {/* Chips με ανάγκες / τιμή / πακέτα */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.2,
            mb: 2,
            width: "100%",
          }}
        >
          {/* Πόσο χρειάζεται συνολικά */}
          <Chip
            label={`${t("needed") || "Χρειάζεται"}: ${readableBaseQuantity(
              reqQtyBase,
              requiredUnit,
              country,
              i18n.language
            )}`}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: { xs: 12.5, sm: 13 } }}
          />

          {/* Τεμάχια (αυγά κτλ) */}
          {isPiece && (
            <Chip
              label={`${t("needed") || "Χρειάζεται"}: ${pieceUnitsNeeded} ${i18n.language === "el" || i18n.language === "gr"
                ? pieceUnitsNeeded === 1
                  ? "αυγό"
                  : "αυγά"
                : pieceUnitsNeeded === 1
                  ? "egg"
                  : "eggs"
                }`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: { xs: 12.5, sm: 13 } }}
            />
          )}

          {/* Προς αγορά με βάση ό,τι έχει ήδη αγοράσει */}
          <Chip
            label={`${t("to_buy") || "Προς αγορά"}: ${readableBaseQuantity(
              remainingBase,
              requiredUnit,
              country,
              i18n.language
            )}`}
            color={remainingBase > 0 ? "warning" : "success"}
            variant="filled"
            sx={{ fontWeight: 600, fontSize: { xs: 12.5, sm: 13 } }}
          />

          {isPiece && (
            <Chip
              label={`${t("to_buy") || "Προς αγορά"}: ${pieceUnitsRemaining < 0 ? 0 : pieceUnitsRemaining
                } ${i18n.language === "el" || i18n.language === "gr"
                  ? "τεμ."
                  : pieceUnitsRemaining === 1
                    ? "pc"
                    : "pcs"
                }`}
              color={remainingBase > 0 ? "warning" : "success"}
              variant="filled"
              sx={{ fontWeight: 600, fontSize: { xs: 12.5, sm: 13 } }}
            />
          )}

          <Chip
            label={`${t("bought_qty") || "Αγοράστηκαν"}: ${readableBaseQuantity(
              purchasedBase,
              requiredUnit,
              country,
              i18n.language
            )}`}
            color="default"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: { xs: 12.5, sm: 13 } }}
          />

          {item.is_completed ? (
            <Chip label={t("item_completed")} color="success" />
          ) : null}
          {item.has_at_home_override ? (
            <Chip label={t("covered_at_home")} color="success" />
          ) : null}
          {item.removed_by_user ? (
            <Chip label={t("removed_from_list")} color="default" />
          ) : null}

          {/* Τιμή ανά kg/L (ή lb/fl oz) */}
          <Chip
            icon={currencySymbol === "$" ? <AttachMoneyIcon /> : <EuroIcon />}
            label={unitPriceLabel}
            color="info"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: { xs: 12.5, sm: 13 } }}
          />

          {showDerivedEggTooltip ? (
            <Tooltip title={eggPricingTooltip}>
              <Chip
                icon={currencySymbol === "$" ? <AttachMoneyIcon /> : <EuroIcon />}
                label={t("per_piece_pricing")}
                color="secondary"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: { xs: 12.5, sm: 13 } }}
              />
            </Tooltip>
          ) : null}
          {priceChangeLabel ? (
            <Chip
              color={Number(priceChange?.delta || 0) < 0 ? "success" : "warning"}
              variant="filled"
              label={priceChangeLabel}
            />
          ) : null}
          {/* Πακέτα που θα αγοραστούν */}
          {packagesNeeded > 0 && (
            <Chip
              label={
                packageSizeLabel
                  ? `${t("packages") || "Πακέτα"
                  }: ${packagesNeeded} × ${packageSizeLabel}`
                  : `${t("packages") || "Πακέτα"}: ${packagesNeeded}`
              }
              color="default"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: { xs: 12.5, sm: 13 } }}
            />
          )}

          {/* Ποσότητα που περισσεύει από τα πακέτα */}
          {packagesNeeded > 0 && Number(leftoverQtyBase || 0) > 0 && (
            <Chip
              label={`${t("leftover_qty") || "Θα περισσέψει"}: ${readableBaseQuantity(
                leftoverQtyBase,
                requiredUnit,
                country,
                i18n.language
              )}`}
              color="secondary"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: { xs: 12.5, sm: 13 } }}
            />
          )}

          {(warningMessage || selectionError) ? (
            <Chip
              label={selectionError || warningMessage}
              color="warning"
              variant="filled"
              sx={{ fontWeight: 700, fontSize: { xs: 12, sm: 12.5 } }}
            />
          ) : null}

          {/* Πραγματικό κόστος για αυτό το προϊόν (πακέτα) */}
          {basketCost != null && (
            <Chip
              icon={currencySymbol === "$" ? <AttachMoneyIcon /> : <EuroIcon />}
              label={`${convertPrice(basketCost)}${currencySymbol} ${t("basket_cost") || "πραγματικό κόστος καλαθιού"
                }`}
              color="success"
              variant="filled"
              sx={{ fontWeight: 700, fontSize: { xs: 12.5, sm: 13 } }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, mb: 1.8, flexWrap: "wrap" }}>
          <Button
            variant="text"
            size="small"
            onClick={() => onOpenHistory?.(item)}
          >
            {t("price_history") || "History"}
          </Button>
          {canAddToWatchlist ? (
            <Button
              variant="text"
              size="small"
              onClick={() => onAddToWatchlist?.(item)}
            >
              {isFollowed
                ? (t("unfollow_price") || "Διακοπή παρακολούθησης")
                : (t("follow_price") || "Παρακολούθηση τιμής")}
            </Button>
          ) : null}
        </Box>

        {/* Επιλογή άλλου store/brand */}
        {canChangeSelection ? (
          <Button
            variant="outlined"
            size="small"
            onClick={handleOpenProductSelection}
            disabled={updating}
            sx={{
              textTransform: "none",
              borderRadius: "18px",
              px: 2,
              py: 0.8,
              mb: 1.8,
              fontWeight: 600,
            }}
          >
            {selectionButtonLabel}
          </Button>
        ) : (
          <Button
            variant="outlined"
            size="small"
            disabled
            sx={{
              textTransform: "none",
              borderRadius: "18px",
              px: 2,
              py: 0.8,
              mb: 1.8,
              fontWeight: 600,
            }}
          >
            {selectionButtonLabel}
          </Button>
        )}
        <ProductSelectionModal
          open={storeModalOpen}
          onClose={() => setStoreModalOpen(false)}
          mode={selectionMode}
          lockedGenericProductId={lockedGenericProductId}
          lockedGenericProductName={lockedGenericProductName}
          genericProducts={genericProducts}
          variants={variants}
          selectedGenericId={selectedGenericId}
          currentVariantId={selectedVariantId}
          searchValue={searchQuery}
          onSearchChange={handleModalSearchChange}
          onSelectGeneric={handleModalSelectGeneric}
          onSelectVariant={handleStoreModalConfirm}
          loadingGenericProducts={loadingGenericProducts}
          loadingVariants={loadingVariants}
          submittingSelection={submittingSelection}
          error={selectionError}
          title={selectionButtonLabel}
          emptyProductsLabel={t("no_generic_products_found")}
          emptyVariantsLabel={t("no_store_variants_found")}
        />

        {/* Πόσο αγόρασε ο χρήστης */}

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mt: 1.5,
            width: "100%",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            label={
              isPiece
                ? t("bought_qty_piece") || "Pieces bought"
                : isUS
                ? t("bought_qty_oz") || "Ποσότητα (oz που αγοράστηκε)"
                : t("bought_qty") || "Ποσότητα που αγοράστηκε"
            }
            type="number"
            value={displayQty}
            size="small"
            sx={{ width: 140, borderRadius: 3 }}
            InputProps={{
              inputProps: { min: 0, step: 1 },
              sx: { background: "transparent", borderRadius: 3 },
            }}
            disabled={readOnly}
            onChange={handleDisplayQtyChange}
          />

          {!readOnly ? (
            <Tooltip title={t("update_quantity") || "Ενημέρωση ποσότητας"}>
              <IconButton
                onClick={handleUpdatePurchase}
                disabled={updating}
                color="primary"
                sx={{
                  bgcolor: "#e7f5e9",
                  borderRadius: "10px",
                  "&:hover": { bgcolor: "#d0edd9" },
                }}
              >
                <CheckIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={t("premium_feature_title") || "Premium λειτουργία"}>
              <IconButton
                onClick={() => onGate?.("update_quantity")}
                sx={{
                  bgcolor: "action.hover",
                  borderRadius: "10px",
                }}
              >
                <LockIcon color="disabled" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {!readOnly ? (
          <Box sx={{ display: "flex", gap: 1, mt: 1.5, width: "100%", flexWrap: "wrap" }}>
            <Button variant="contained" size="small" onClick={() => handleProgressAction({ action: "mark_complete" })} disabled={updating || isCovered}>
              {t("bought_all")}
            </Button>
            <Button variant="outlined" size="small" onClick={handleUpdatePurchase} disabled={updating}>
              {t("bought_part")}
            </Button>
            <Button variant="outlined" size="small" onClick={() => handleProgressAction({ action: "mark_at_home" })} disabled={updating || progressStatus === "at_home"}>
              {t("already_at_home")}
            </Button>
            <Button color="inherit" variant="text" size="small" onClick={() => handleProgressAction({ action: "remove" })} disabled={updating || progressStatus === "removed"}>
              {t("remove_from_list")}
            </Button>
          </Box>
        ) : null}

        {/* Κάτω σημείωση: θεωρητικό vs πραγματικό κόστος */}
        <Box sx={{ mt: 2.5, ml: 0.5 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 600,
              fontSize: { xs: 14, sm: 14.5 },
            }}
          >
            {t("theoretical_cost") ||
              "Θεωρητικό κόστος (ακριβής ποσότητα)"}{" "}
            :{" "}
            <b>
              {theoreticalCost != null ? `${convertPrice(theoreticalCost)}${currencySymbol}` : "—"}
            </b>
          </Typography>
          <Typography
            variant="body2"
            color="text.primary"
            sx={{
              fontWeight: 700,
              fontSize: { xs: 15, sm: 15.5 },
              mt: 0.3,
            }}
          >
            {t("basket_cost") ||
              "Πραγματικό κόστος καλαθιού (πακέτα)"}{" "}
            :{" "}
            <b>
              {basketCost != null ? `${convertPrice(basketCost)}${currencySymbol}` : "—"}
            </b>
          </Typography>
          {isUS ? (
            <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
              {t("us_retailer_provider_estimate_note") || "InCooking estimate from retailer provider data in your shopping area. Checkout totals may differ."}
            </Typography>
          ) : null}
        </Box>
      </ListItem>
    </Grid>
  );
}
